import { useState, type CSSProperties, type ReactNode } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ArrowRight, Check } from 'lucide-react'
import clsx from 'clsx'
import { Logo, Wordmark } from '@/components/Logo'
import { PlatformLogo } from '@/components/PlatformLogo'
import { PLATFORM_LIST, PLATFORMS } from '@/lib/platforms'
import { useStore } from '@/lib/store'
import { trackEvent } from '@/lib/posthog'
import { completeOnboarding } from '@/lib/data'
import { ConnectChannelsModal } from '@/features/channels/ConnectChannelsModal'
import { OnboardingRewards } from './OnboardingRewards'

const ONBOARDING_PLATFORMS = PLATFORM_LIST.filter((p) =>
  ['bluesky', 'mastodon', 'linkedin', 'x', 'threads', 'facebook', 'instagram'].includes(p.id),
)

/* Hand-drawn marker doodles, matching the smiley on the sticky-note logo. */
const doodleProps = {
  viewBox: '0 0 32 32',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const

function CalendarDoodle() {
  return (
    <svg {...doodleProps} className="h-8 w-8">
      <path d="M5 8.5c6.2-.9 15.8-1 22-.4-.3 5.8.2 11.6-.4 17.2-7 .8-14.2.8-21.2.3-.5-5.6-.7-11.4-.4-17.1Z" />
      <path d="M10.5 4.5v5.5M21.5 4.5v5.5" />
      <path d="M10.5 17.5l3.6 3.4 7.4-6.7" />
    </svg>
  )
}

function PreviewDoodle() {
  return (
    <svg {...doodleProps} className="h-8 w-8">
      <path d="M7 4.5c6-.5 12-.4 18 .2.5 7.4.5 14.8-.1 22.2-6 .5-11.9.4-17.8-.2-.6-7.4-.6-14.8-.1-22.2Z" />
      <circle cx="11" cy="10" r="2" />
      <path d="M15 8.8h6M15 11.2h4M10 16h12M10 19h12M10 22h7" />
    </svg>
  )
}

function ChartDoodle() {
  return (
    <svg {...doodleProps} className="h-8 w-8">
      <path d="M6.5 5c-.4 7-.3 14 .3 21 6.6.6 13.2.5 19.7.2" />
      <path d="M11 21c2.4-2.2 3.7-6.1 6.2-5.1 2.4 1 3.3-5.7 7.8-8.4" />
      <path d="M21.5 7.2 25 7.5l-.4 3.4" />
    </svg>
  )
}

function SmileyDoodle() {
  return (
    <svg {...doodleProps} strokeWidth={2.4} className="h-12 w-12">
      <circle cx="11" cy="12" r="1.1" fill="currentColor" />
      <circle cx="21" cy="12" r="1.1" fill="currentColor" />
      <path d="M10 18.5c3.9 3.5 8.5 3.3 12-.4" />
    </svg>
  )
}

function StickyNote({
  tilt,
  color,
  delay = 0,
  className,
  children,
}: {
  tilt: number
  color: 'blue' | 'yellow' | 'pink'
  delay?: number
  className?: string
  children: ReactNode
}) {
  return (
    <div className="note-shadow">
      <div
        className={clsx(
          'sticky-note note-tilt note-in',
          { blue: 'bg-note-blue', yellow: 'bg-note-yellow', pink: 'bg-note-pink' }[color],
          className,
        )}
        style={{ '--tilt': `${tilt}deg`, animationDelay: `${delay}ms` } as CSSProperties}
      >
        {children}
      </div>
    </div>
  )
}

const FEATURES = [
  {
    doodle: CalendarDoodle,
    color: 'yellow' as const,
    tilt: -1.8,
    title: 'Schedule everywhere',
    desc: 'One calendar for every network.',
  },
  {
    doodle: PreviewDoodle,
    color: 'blue' as const,
    tilt: 1.4,
    title: 'Real previews',
    desc: 'See each network before you publish.',
  },
  {
    doodle: ChartDoodle,
    color: 'pink' as const,
    tilt: -1.2,
    title: 'Analytics',
    desc: 'See what works, all in one place.',
  },
]

export function Onboarding() {
  const navigate = useNavigate()
  const onboarded = useStore((s) => s.onboarded)
  const channels = useStore((s) => s.channels)
  const setOnboarded = useStore((s) => s.setOnboarded)
  const [step, setStep] = useState(0)
  const [connectOpen, setConnectOpen] = useState(false)
  const [finishing, setFinishing] = useState(false)
  const [finishError, setFinishError] = useState('')

  const finish = async () => {
    setFinishing(true)
    setFinishError('')
    try {
      await completeOnboarding()
      setOnboarded(true)
      trackEvent('onboarding_completed', { channels_connected: channels.length })
      navigate('/calendar')
    } catch {
      setFinishError('Could not finish setup. Try again.')
      setFinishing(false)
    }
  }

  if (onboarded) return <Navigate to="/calendar" replace />

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-paper p-4">
      {/* stray notes on the desk, behind the card */}
      <div
        aria-hidden
        className="absolute left-[6%] top-[12%] hidden h-24 w-24 rotate-[-9deg] bg-note-yellow opacity-60 sm:block"
      />
      <div
        aria-hidden
        className="absolute bottom-[14%] right-[5%] hidden h-20 w-20 rotate-[7deg] bg-note-pink opacity-50 sm:block"
      />
      <div
        aria-hidden
        className="absolute bottom-[30%] left-[3%] hidden h-14 w-14 rotate-[5deg] bg-note-blue opacity-50 lg:block"
      />

      <div className="relative w-full max-w-4xl">
        {/* progress */}
        <div className="mb-6 flex items-center justify-center gap-2">
          {['Welcome', 'Connect channels', 'All set'].map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={clsx(
                  'flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition',
                  i === step
                    ? 'bg-brand-500 text-white'
                    : i < step
                      ? 'bg-brand-50 text-brand-600'
                      : 'bg-panel text-ink-400 border border-line',
                )}
              >
                {i < step ? <Check size={13} /> : <span className="font-bold">{i + 1}</span>}
                {s}
              </div>
              {i < 2 && <div className="h-px w-6 bg-line" />}
            </div>
          ))}
        </div>

        <div className="card overflow-hidden p-8 animate-scale-in sm:p-10">
          {step === 0 && (
            <div className="text-center">
              <div className="mx-auto mb-6 flex justify-center">
                <Logo size={64} />
              </div>
              <h1 className="font-display text-3xl font-extrabold tracking-tight text-ink-900 sm:text-4xl">
                Welcome to Hey Tasky
              </h1>
              <p className="mx-auto mt-3 max-w-md text-ink-500">
                The friendly way to plan, write and publish your social content, across every
                platform, from one place.
              </p>
              <div className="mx-auto mt-9 grid max-w-[38rem] gap-5 sm:grid-cols-3 sm:gap-6">
                {FEATURES.map((f, i) => (
                  <StickyNote
                    key={f.title}
                    color={f.color}
                    tilt={f.tilt}
                    delay={120 + i * 90}
                    className="mx-auto flex aspect-square w-full max-w-[11.5rem] flex-col p-4 text-left text-ink-900"
                  >
                    <f.doodle />
                    <div className="mt-auto font-note text-[1.4rem] font-bold leading-[1.05]">
                      {f.title}
                    </div>
                    <div className="mb-1 mt-1 text-[12.5px] leading-snug text-ink-700">{f.desc}</div>
                  </StickyNote>
                ))}
              </div>
              <button onClick={() => setStep(1)} className="btn-primary mx-auto mt-9">
                Get started <ArrowRight size={16} />
              </button>
            </div>
          )}

          {step === 1 && (
            <div>
              <div className="text-center">
                <h2 className="font-display text-2xl font-bold text-ink-900">Connect your channels</h2>
                <p className="mx-auto mt-2 max-w-md text-sm text-ink-500">
                  Use the real connection flow now, or skip and add channels later.
                </p>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {ONBOARDING_PLATFORMS.map((p) => {
                  const count = channels.filter((c) => c.platform === p.id).length
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setConnectOpen(true)}
                      className="group relative flex flex-col items-center gap-2 rounded-2xl border border-line bg-surface p-4 shadow-card transition-all hover:-translate-y-0.5 hover:border-brand-500/60 hover:shadow-glow"
                    >
                      {count > 0 && (
                        <span className="absolute right-2 top-2 grid h-5 min-w-5 place-items-center rounded-full bg-brand-500 px-1 text-[11px] font-bold text-white">
                          {count}
                        </span>
                      )}
                      <PlatformLogo platform={p.id} size={44} />
                      <span className="text-sm font-semibold text-ink-800">{p.name}</span>
                      <span
                        className={clsx(
                          'flex items-center gap-1 text-[11px] font-medium',
                          count > 0 ? 'text-accent-green' : 'text-ink-400',
                        )}
                      >
                        {count > 0 ? (
                          <>
                            <Check size={11} /> Connected
                          </>
                        ) : (
                          'Connect'
                        )}
                      </span>
                    </button>
                  )
                })}
              </div>
              <div className="mt-7 flex items-center justify-between">
                <button onClick={() => setStep(0)} className="btn-subtle text-sm">
                  Back
                </button>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-ink-400">{channels.length} connected</span>
                  <button onClick={() => setConnectOpen(true)} className="btn-ghost">
                    Connect accounts
                  </button>
                  <button onClick={() => setStep(2)} className="btn-primary">
                    {channels.length ? 'Continue' : 'Skip for now'} <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="text-center">
              <div className="mx-auto mb-6 flex justify-center">
                <StickyNote
                  color="blue"
                  tilt={-3}
                  className="grid h-28 w-28 place-items-center text-ink-900"
                >
                  <SmileyDoodle />
                </StickyNote>
              </div>
              <h2 className="font-display text-2xl font-bold text-ink-900">You're all set!</h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-ink-500">
                {channels.length > 0
                  ? `${channels.length} channel${channels.length > 1 ? 's' : ''} connected. Time to plan your first post.`
                  : 'Jump into the calendar and connect channels whenever you like.'}
              </p>
              {channels.length > 0 && (
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {channels.map((c) => (
                    <span
                      key={c.id}
                      className="flex items-center gap-1.5 rounded-full border border-line bg-panel py-1 pl-1 pr-3"
                    >
                      <span
                        className="grid h-6 w-6 place-items-center rounded-full text-white"
                        style={{ background: PLATFORMS[c.platform].color === '#ffffff' ? '#111' : PLATFORMS[c.platform].color }}
                      >
                        <span className="h-3.5 w-3.5">{PLATFORMS[c.platform].icon}</span>
                      </span>
                      <span className="text-xs font-medium text-ink-800">{c.name}</span>
                    </span>
                  ))}
                </div>
              )}
              <OnboardingRewards className="mt-8 text-left" />

              {finishError && <p className="mt-4 text-sm text-accent-red">{finishError}</p>}

              <button onClick={finish} disabled={finishing} className="btn-primary mx-auto mt-8">
                {finishing ? 'Opening...' : 'Open Hey Tasky'} <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-center opacity-70">
          <Wordmark size={28} />
        </div>
      </div>
      <ConnectChannelsModal open={connectOpen} onClose={() => setConnectOpen(false)} />
    </div>
  )
}
